<template>
  <section id="courses-section" class="categories">
    <div class="categories-container">
      <button
        type="button"
        class="section-header-link"
        @click="scrollToCategoriesGrid"
      >
        <span class="section-label">{{ t('index.categories.label') }}</span>
        <h2 class="section-title">{{ t('index.categories.title') }}</h2>
      </button>

      <!-- Скелетон только пока грузятся данные с бэка -->
      <div v-if="loadingCategories" class="categories-grid categories-grid--skeleton" aria-hidden="true">
        <div v-for="i in 4" :key="i" class="category-card-skeleton" />
      </div>

      <template v-else>
        <div id="categories-grid" ref="categoriesGridRef" class="categories-grid categories-grid--desktop">
          <CategoryCard
            v-for="category in desktopCategories"
            :key="category.id"
            :title="category.title"
            :image="category.image"
            :image-mobile="category.imageMobile"
            :image-alt="category.imageAlt"
            :image-mobile-alt="category.imageMobileAlt"
            :href="category.href"
            :bg-color="category.bgColor"
          />
        </div>

        <div id="categories-grid-mobile" class="categories-grid categories-grid--mobile">
          <CategoryCard
            v-for="category in mobileCategories"
            :key="category.id"
            :title="category.title"
            :image="category.image"
            :image-mobile="category.imageMobile"
            :image-alt="category.imageAlt"
            :image-mobile-alt="category.imageMobileAlt"
            :href="category.href"
            :bg-color="category.bgColor"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useCourseCategory } from '@/composables/course/useCourseCategory'

const { t } = useI18n()
const { landingCategories, loadingCategories } = useCourseCategory()
const categoriesGridRef = ref<HTMLElement | null>(null)

const desktopCategories = landingCategories
const MOBILE_CATEGORY_ORDER: string[] = [
  'biblical',
  'family',
  'health',
  'womens',
  'psychology',
  'youth',
  'teenage',
  'childish',
  'prayer',
] // Міняй порядок ID тут, якщо для мобілки потрібна інша послідовність

const mobileCategories = computed(() => {
  const pos = new Map(MOBILE_CATEGORY_ORDER.map((id, idx) => [id, idx]))
  return [...landingCategories.value].sort(
    (a, b) => (pos.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (pos.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  )
})

function scrollToCategoriesGrid () {
  nextTick(() => {
    const el = categoriesGridRef.value ?? document.getElementById('categories-grid')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}
</script>

<style scoped>
.categories {
  padding: 95px 0 95px;
  background: #fff;
  max-width: var(--max-width-container);
  margin: 0 auto;
}

.categories-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  text-align: center;
}

.section-header-link {
  display: block;
  width: 100%;
  border: none;
  background: none;
  padding: 0;
  text-align: center;
  font: inherit;
  color: inherit;
  margin-bottom: 68px;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(4, 300px);
  gap: 36px;
  justify-content: center;
}

.categories-grid--desktop {
  display: grid;
}

.categories-grid--mobile {
  display: none;
}

@media (max-width: 1400px) {
  .categories-container {
    padding: 0 60px;
  }

  .categories-grid {
    grid-template-columns: repeat(3, 300px);
  }
}

@media (max-width: 1024px) {
  .categories-grid {
    grid-template-columns: repeat(2, 300px);
  }
}

@media (max-width: 991px) {
  .categories {
    padding: 60px 0;
  }

  .categories-container {
    padding: 0 30px;
  }
}
@media (max-width: 768px) {
  .categories-grid {
    grid-template-columns: repeat(1, 300px);
  }
}
@media (max-width: 480px) {
  .categories {
    padding: 40px 0 64px 0;
  }
  .section-desc{
    display: none;
  }

  .categories-container {
    padding: 0 20px;
  }

  .categories-grid--desktop {
    display: none;
  }

  .categories-grid--mobile {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

/* Скелетон грида категорій (поки дані з API) */
.category-card-skeleton {
  width: 300px;
  height: 261px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    var(--color-bg-faq, #eee) 0%,
    #e8e8e8 50%,
    var(--color-bg-faq, #eee) 100%
  );
  background-size: 200% 100%;
  background-position: 0 0;
  animation: categories-skeleton-shimmer 1.2s ease-in-out infinite;
}

.categories-grid--skeleton {
  margin: 0 auto;
}

@keyframes categories-skeleton-shimmer {
  to {
    background-position: 200% 0;
  }
}
</style>

